import { useState, useEffect } from 'react'
import { useRegisterSW } from 'virtual:pwa-register/react'

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

export function usePwaInstall() {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [isInstalled, setIsInstalled] = useState(
    () => window.matchMedia('(display-mode: standalone)').matches ||
          (navigator as Navigator & { standalone?: boolean }).standalone === true
  )

  useEffect(() => {
    const handler = (e: Event) => {
      e.preventDefault()
      setInstallPrompt(e as BeforeInstallPromptEvent)
    }

    window.addEventListener('beforeinstallprompt', handler)
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true)
      setInstallPrompt(null)
    })

    return () => window.removeEventListener('beforeinstallprompt', handler)
  }, [])

  const install = async () => {
    if (!installPrompt) return false
    await installPrompt.prompt()
    const { outcome } = await installPrompt.userChoice
    if (outcome === 'accepted') {
      setInstallPrompt(null)
      return true
    }
    return false
  }

  const canInstall = !!installPrompt && !isInstalled

  return { canInstall, isInstalled, install }
}

export function useIosInstall() {
  const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent)
  const isStandalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem('ios-install-dismissed') === '1'
  )

  const dismiss = () => {
    localStorage.setItem('ios-install-dismissed', '1')
    setDismissed(true)
  }

  return { show: isIos && !isStandalone && !dismissed, dismiss }
}

export function usePwaUpdate() {
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(r) {
      // Verifica atualizações a cada hora
      if (r) {
        setInterval(() => r.update().catch(() => { /* transient fetch error, ignore */ }), 60 * 60 * 1000)
      }
    },
    onRegisterError(error) {
      // SW registration failed (e.g. unsupported browser, private mode) — not critical
      console.warn('[SW] Registration error:', error)
    },
  })

  return { needRefresh, updateServiceWorker }
}
